import { SxProps } from '@mui/material';

import { useMytaverse } from '@providers/MytaverseProvider';
import { colors } from '@constants/colors';
import { ScreenSizes } from '@interfaces';
import { useTheme } from '@mui/material/styles';

export const useStyles = () => {
  const { appTheme } = useMytaverse();
  const { breakpoints } = useTheme();
  const { backgroundColorHover } = appTheme.submitButton;

  return {
    root: {
      '& .MuiBackdrop-root': {
        backgroundColor: '#00000066',
        backdropFilter: ' blur(12px)',
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '24px 32px 28px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 460,
      background: colors.white,
      borderRadius: '16px',
      boxShadow: 24,

      '&:focus-visible': {
        outline: 'none',
      },

      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        padding: '20px 24px',
        width: '90%',
        maxWidth: 420,
      },
    } as SxProps,
    iconContainer: {
      display: 'flex',
      background: appTheme.mainColor,
      borderRadius: '50%',
      padding: 0.75,
      '& svg': {
        color: colors.white,
        width: 18,
        height: 18,
      },
    },
    closeButton: {
      position: 'absolute',
      top: 8,
      right: 8,
      padding: 1,
      color: colors.black,
    },
    closeIcon: {
      width: 14,
      height: 14,
    },
    avatarWrapper: {
      position: 'relative',
      width: 88,
      height: 88,
      boxSizing: 'border-box',
      borderRadius: '50%',
      // Always present so the block does not jump by the border width once a
      // file is dragged over it.
      border: '2px dashed transparent',
      transition: 'border-color 120ms ease, background-color 120ms ease',

      '& .MuiAvatar-root': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        fontSize: 28,
      },
    },
    avatarWrapperDragging: {
      borderColor: appTheme.mainColor,
      backgroundColor: colors.white_20,
    },
    avatarUploadButton: {
      position: 'absolute',
      right: -4,
      bottom: -4,
      width: 30,
      height: 30,
      padding: 0,
      background: appTheme.mainColor,
      color: colors.white,

      '&:hover': {
        background: backgroundColorHover,
      },

      '&.Mui-disabled': {
        background: appTheme.mainColor,
        color: colors.white,
      },
    },
    avatarUploadIcon: {
      width: 16,
      height: 16,
    },
    avatarUploadSpinner: {
      color: colors.white,
    },
    titleRow: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
    },
    savingIndicator: {
      color: colors.white,
      flexShrink: 0,
    },
    saveErrorRow: {
      display: 'flex',
      justifyContent: 'center',
    },
    saveErrorIcon: {
      flexShrink: 0,
    },
    imageErrorText: {
      fontFamily: 'var(--primary-font-medium)',
      fontSize: 12,
      lineHeight: 1.4,
      color: colors.red_second,
      textAlign: 'center',
    },
    title: {
      fontFamily: 'var(--primary-font-medium)',
      fontSize: 20,
      lineHeight: 1.3,
      color: colors.black,
      marginTop: 0.75,
      textAlign: 'center',
    },
    subtitle: {
      fontFamily: 'var(--primary-font-medium)',
      fontSize: 14,
      lineHeight: 1.4,
      color: colors.black,
      marginTop: 0.25,
      textAlign: 'center',
      opacity: 0.7,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: '100%',
      marginTop: 2,
    },
    input: {
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        fontSize: 14,
        fontFamily: 'var(--primary-font-medium)',
        '& .MuiInputLabel-root': {
          fontFamily: 'var(--primary-font-medium)',
          fontSize: 14,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: appTheme.mainColor,
          borderWidth: 2,
        },
      },
    },
    genderLabel: {
      fontFamily: 'var(--primary-font-medium)',
      fontSize: 14,
      color: 'rgba(0, 0, 0, 0.6)',
      marginBottom: 0.5,
      display: 'block',
    },
    formControl: {
      width: '100%',
      alignItems: 'flex-start',
    },
    radioGroup: {
      flexDirection: 'row',
      gap: 2,
      justifyContent: 'flex-start',
      '& .MuiFormControlLabel-label': {
        fontFamily: 'var(--primary-font-medium)',
        fontSize: 14,
      },
    },
    radio: {
      color: appTheme.mainColor,
      '&.Mui-checked': {
        color: appTheme.mainColor,
      },
    },
    button: {
      padding: '10px 24px',
      width: '100%',
      fontFamily: 'var(--primary-font-medium)',
      fontWeight: 500,
      borderRadius: '8px',
      fontSize: 14,
      color: colors.white,
      backgroundColor: appTheme.mainColor,
      textTransform: 'none',
      marginTop: 0.5,

      '&:hover': {
        backgroundColor: backgroundColorHover,
      },

      '&:disabled': {
        color: colors.white,
        background: colors.grey_third,
        cursor: 'no-drop',
      },
    },
    actions: {
      display: 'flex',
      gap: 1.5,
      width: '100%',

      '& .MuiButton-root': {
        flex: 1,
        marginTop: 0,
      },
    },
    secondaryButton: {
      padding: '10px 24px',
      fontFamily: 'var(--primary-font-medium)',
      fontWeight: 500,
      borderRadius: '8px',
      fontSize: 14,
      color: colors.black,
      backgroundColor: 'transparent',
      border: `1px solid ${colors.grey_third}`,
      textTransform: 'none',

      '&:hover': {
        backgroundColor: colors.grey_light,
      },
    },
  };
};
